import queryString from 'query-string';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import { DefaultSearchType } from 'algolia-api/types/DefaultSearchType';
import { DisplayedType } from 'algolia-api/types/DisplayedTypes';
import { ListingType } from 'algolia-api/types/ListingType';
import { SearchLocation } from 'algolia-api/types/SearchLocation';
import {
  clearSearchLocation,
  setPageIndex,
  setSearchFilter,
  setSearchQuery,
  setUserInitiatedSearch,
  updateSearchLocation,
} from 'modules/main/store/ducks/mainSearch.actions';
import { showModal } from 'modules/main/store/ducks/modal.actions';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useMainSelector } from 'modules/main/store/hooks/useMainSelector';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { EMPTY_SEARCH_LOCATION } from 'utils/constants/general/emptySearchLocation';
import { listingTypesBySection } from 'utils/constants/general/listingTypesBySection';
import { isDefined } from 'utils/functional';
import { listingSearchHome } from 'utils/searchHomePages';
import { truncateToUTF8Bytes } from 'utils/string/truncateToUTF8Bytes';
import { getFirstParam } from 'utils/url';

type Args = {
  searchFrom: string | undefined;
  searchMode: boolean;
  defaultSearchType: DefaultSearchType | undefined;
};

export function useMainSearchMenu({
  searchFrom,
  searchMode,
  defaultSearchType,
}: Args) {
  const dispatch = useMainDispatch();
  const reduxSearchLocation = useMainSelector(
    (state) => state.mainSearch.searchLocation,
  );
  const reduxQuery = useMainSelector((state) => state.mainSearch.query || '');
  const reduxListingType = useMainSelector(
    (state) => state.mainSearch.filters?.type,
  );

  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { user } = useUserEnvironment();
  const [clearing, setClearing] = useState(false);
  const [isRemote, setIsRemote] = useState(false);
  const [listingTypes] = useState<Array<DisplayedType>>(
    listingTypesBySection.MAIN,
  );
  const [listingType, setListingType] = useState<
    ListingType | DefaultSearchType
  >(reduxListingType || defaultSearchType || listingTypes[0]);

  const initialQuery =
    (isDefined(getFirstParam(location, 'q'))
      ? getFirstParam(location, 'q')
      : reduxQuery) || '';

  const [query, setQuery] = useState(truncateToUTF8Bytes(initialQuery, 512));
  const [enqueueSearch, setEnqueueSearch] = useState([false, null]);
  const [searchLocation, setSearchLocation] = useState<
    SearchLocation | null | undefined
  >(reduxSearchLocation);

  useEffect(() => {
    if (defaultSearchType) {
      setListingType(defaultSearchType);
    }
  }, [defaultSearchType]);

  useEffect(() => {
    setSearchLocation(reduxSearchLocation);
  }, [reduxSearchLocation]);

  useEffect(() => {
    if (navigationType === 'POP' && !clearing) {
      const queryParams = queryString.parse(location.search);

      if (typeof queryParams.q === 'string' && queryParams.q !== reduxQuery) {
        setQuery(truncateToUTF8Bytes(queryParams.q || '', 512));
      }
    }

    if (clearing) {
      setClearing(false);
    }
  }, [clearing, location.search, navigationType, reduxQuery]);

  const onLocationChange = (
    newSearchLocation: SearchLocation | null | undefined,
    shouldEnqueueSearch = true,
    setRemote = false,
  ) => {
    setSearchLocation(newSearchLocation);

    if (!newSearchLocation) {
      dispatch(updateSearchLocation(EMPTY_SEARCH_LOCATION, true));
    }

    if (setRemote) {
      dispatch(setSearchFilter('locationTypeFacet', ['REMOTE']));
      setIsRemote(true);
    }

    if (shouldEnqueueSearch) {
      // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'boolean |... Remove this comment to see the full error message
      setEnqueueSearch([true, query]);
    }
  };

  const search = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (stateQuery: any) => {
      const finalQuery = stateQuery;

      batch(() => {
        const mainSearchLocationAddress = reduxSearchLocation
          ? // @ts-expect-error should latLongAddress exist on SearchLocation?
            reduxSearchLocation.latLongAddress
          : undefined;

        const searchLocationAddress = searchLocation
          ? // @ts-expect-error TS(2339): Property 'latLongAddress' does not exist on type '... Remove this comment to see the full error message
            searchLocation.latLongAddress
          : undefined;

        const locationsMatch =
          mainSearchLocationAddress === searchLocationAddress;

        if (!locationsMatch) {
          if (searchLocation) {
            dispatch(updateSearchLocation(searchLocation, true)).then(() =>
              dispatch(setPageIndex(0, false)),
            );
          } else {
            dispatch(clearSearchLocation(true)).then(() =>
              dispatch(setPageIndex(0, false)),
            );
          }
        }

        if (reduxListingType && listingType !== reduxListingType) {
          dispatch(setSearchFilter('type', listingType));

          if (listingType === 'VOLOP') {
            setSearchFilter('actionTypeFacet', ['VOLOP']);
          }
        }

        dispatch(setSearchQuery(stateQuery, true));
      });

      const searchHome = listingSearchHome(listingType);
      const queryParams = queryString.parse(location.search);
      // FIXME: Next line has a bug that ESLint started to warn about when updating
      //        JS packages. It should be fixed, but it breaks a test.
      // eslint-disable-next-line valid-typeof
      const hasArticleSearch = typeof queryParams.pq !== undefined;

      let additionalParams = '';

      if (isRemote) {
        additionalParams = '&locationType=REMOTE';
      }

      if (searchHome && (!searchMode || (!queryParams.q && hasArticleSearch))) {
        if (finalQuery && finalQuery === reduxQuery) {
          navigate(
            `${searchHome}?q=${encodeURIComponent(
              finalQuery,
            )}${additionalParams}`,
            {
              state: {
                searchFrom,
              },
              replace: true,
            },
          );
        } else {
          navigate(
            `${searchHome}?q=${encodeURIComponent(
              finalQuery,
            )}${additionalParams}`,
            {
              state: {
                searchFrom,
              },
            },
          );
        }

        dispatch(setUserInitiatedSearch());
      }
    },
    [
      searchLocation,
      reduxSearchLocation,
      isRemote,
      listingType,
      location.search,
      searchMode,
      reduxListingType,
      dispatch,
      reduxQuery,
      navigate,
      searchFrom,
    ],
  );

  const redirectTo = location ? `${location.pathname}${location.search}` : '';

  const clickLogin = (e: SyntheticEvent) => {
    e.preventDefault();
    trackClicked('Log In Search Hero');
    dispatch(
      showModal('LOGIN', {
        redirectTo,
      }),
    );
  };

  const clickSignup = (e: SyntheticEvent) => {
    e.preventDefault();
    trackClicked('Sign Up Search Hero');
    dispatch(
      showModal('SIGNUP', {
        redirectTo,
      }),
    );
  };

  const redirectParam = queryString.stringify({
    to: redirectTo,
  });

  useEffect(() => {
    const [enqueued, searchQuery] = enqueueSearch;

    if (enqueued) {
      setEnqueueSearch([false, null]);
      search(searchQuery || '');
    }
  }, [enqueueSearch, search]);

  const clearSearchInput = useCallback(() => {
    setClearing(true);
    setQuery('');

    if (searchMode) {
      dispatch(setSearchQuery('', true));
    }
  }, [dispatch, setQuery, searchMode]);

  const selectedType =
    listingTypes.find((l) => l === listingType) ||
    (listingTypes.includes('JOB') ? 'JOB' : listingTypes[0]);

  return {
    listingTypes,
    selectedType,
    initialQuery,
    query,
    searchLocation,
    setListingType,
    setQuery,
    clearSearchInput,
    redirectParam,
    onLocationChange,
    search,
    clickLogin,
    clickSignup,
    showLogin: !user,
  };
}
